import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Plus, AlertOctagon, Trash2, CalendarCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { PageHeader, Field, NativeSelect, Empty } from "../components/common";
import { api, errMsg, useFetch } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useT } from "../lib/i18n";
import { useIsNarrow } from "../lib/responsive";

const TONE = { assignment: "bg-[#db0720] text-white", travel: "bg-sky-600 text-white", absence: "bg-amber-400 text-black" };
const KINDS = ["vacation", "sick", "training", "holiday", "other"];
const pad = (n) => String(n).padStart(2, "0");
const toLocal = (iso) => { const d = new Date(iso); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`; };
const hm = (iso) => { const d = new Date(iso); return `${pad(d.getHours())}:${pad(d.getMinutes())}`; };
const startOfWeek = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

const Conflicts = ({ list }) => {
  const { t } = useT();
  if (!list?.length) return null;
  return (
    <div data-testid="schedule-conflicts" className="rounded border border-red-300 bg-red-50 p-3 text-xs text-red-800">
      <div className="mb-1 flex items-center gap-1 font-bold"><AlertOctagon className="h-4 w-4" />{t("sch.conflict")}</div>
      {list.map((c) => <div key={c.id}>• {t(`sch.type.${c.event_type}`)} {c.folio || c.location || (c.absence_kind && t(`sch.kind.${c.absence_kind}`)) || ""}: {new Date(c.start).toLocaleString()} → {new Date(c.end).toLocaleString()}</div>)}
    </div>
  );
};

function EventDialog({ ev, techs, quotes, onClose, onSaved }) {
  const { t } = useT();
  const { can } = useAuth();
  const [f, setF] = useState(ev);
  const [conflicts, setConflicts] = useState([]);
  const [qMachines, setQMachines] = useState([]);
  useEffect(() => {
    if (f.event_type !== "assignment" || !f.quotation_id) { setQMachines([]); return; }
    api.get(`/quotations/${f.quotation_id}`).then((r) => setQMachines(r.data.resolved?.machines || [])).catch(() => setQMachines([]));
  }, [f.event_type, f.quotation_id]);
  const body = () => ({ ...f, start: new Date(f.start).toISOString(), end: new Date(f.end).toISOString() });
  const check = async () => {
    try { const r = await api.post("/schedule/check", body()); setConflicts(r.data.conflicts); if (!r.data.conflicts.length) toast.success(t("sch.available")); } catch (e) { toast.error(errMsg(e)); }
  };
  const save = async () => {
    try {
      if (f.id) await api.put(`/schedule/${f.id}`, body()); else await api.post("/schedule", body());
      toast.success(t("common.saved")); onSaved();
    } catch (e) {
      if (e.response?.status === 409 && e.response.data?.detail?.conflicts) { setConflicts(e.response.data.detail.conflicts); toast.error(t("sch.conflict")); return; }
      toast.error(errMsg(e));
    }
  };
  const cancelEv = async () => {
    if (!window.confirm(t("sch.cancelConfirm"))) return;
    try { await api.delete(`/schedule/${f.id}`); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  const ro = !can("schedule:write");
  const sel = new Set(f.machine_ids || []);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="schedule-event-dialog">
        <DialogHeader><DialogTitle>{f.id ? t("sch.editEvent") : t("sch.newEvent")}</DialogTitle></DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t("ws.type")} required>
            <NativeSelect testid="event-type-select" disabled={ro} value={f.event_type} onChange={(v) => { setF({ ...f, event_type: v }); setConflicts([]); }}
              options={["assignment", "travel", "absence"].map((x) => ({ value: x, label: t(`sch.type.${x}`) }))} />
          </Field>
          <Field label={t("nav.technicians")} required>
            <NativeSelect testid="event-tech-select" disabled={ro} value={f.technician_id} placeholder="—" onChange={(v) => { setF({ ...f, technician_id: v }); setConflicts([]); }}
              options={techs.map((x) => ({ value: x.id, label: `${x.name} (${x.country})` }))} />
          </Field>
          <Field label={t("sch.start")} required><Input data-testid="event-start-input" disabled={ro} type="datetime-local" value={f.start} onChange={(e) => { setF({ ...f, start: e.target.value }); setConflicts([]); }} /></Field>
          <Field label={t("sch.end")} required><Input data-testid="event-end-input" disabled={ro} type="datetime-local" value={f.end} onChange={(e) => { setF({ ...f, end: e.target.value }); setConflicts([]); }} /></Field>
          {f.event_type === "assignment" && (
            <Field label={t("nav.quotations")} required className="sm:col-span-2">
              <NativeSelect testid="event-quote-select" disabled={ro} value={f.quotation_id} placeholder="—" onChange={(v) => setF({ ...f, quotation_id: v, machine_ids: [] })}
                options={quotes.map((q) => ({ value: q.id, label: `${q.folio} · ${q.customer_name} · ${t(`status.${q.status}`)}` }))} />
            </Field>
          )}
          {f.event_type === "assignment" && qMachines.length > 0 && (
            <Field label={t("ws.machines")} className="sm:col-span-2">
              <div className="flex flex-wrap gap-3">{qMachines.map((m) => (
                <label key={m.id} className="flex items-center gap-1.5 text-xs"><Checkbox disabled={ro} checked={sel.has(m.id)} onCheckedChange={(c) => { const s = new Set(sel); c ? s.add(m.id) : s.delete(m.id); setF({ ...f, machine_ids: [...s] }); }} />{m.model} #{m.serial_number}</label>
              ))}</div>
            </Field>
          )}
          {f.event_type === "absence" && (
            <Field label={t("sch.kind")}><NativeSelect testid="event-kind-select" disabled={ro} value={f.absence_kind || "vacation"} onChange={(v) => setF({ ...f, absence_kind: v })} options={KINDS.map((k) => ({ value: k, label: t(`sch.kind.${k}`) }))} /></Field>
          )}
          {f.event_type !== "absence" && (
            <Field label={t("sch.location")}><Input data-testid="event-location-input" disabled={ro} value={f.location || ""} onChange={(e) => setF({ ...f, location: e.target.value })} /></Field>
          )}
          <Field label={t("common.notes")} className="sm:col-span-2"><Textarea data-testid="event-notes-input" disabled={ro} rows={2} value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Field>
        </div>
        <Conflicts list={conflicts} />
        <p className="text-[11px] text-neutral-500">{t("sch.blockNote")}</p>
        {!ro && (
          <DialogFooter className="flex-wrap gap-2">
            {f.id && <Button variant="ghost" data-testid="event-cancel-btn" onClick={cancelEv} className="text-[#db0720] sm:mr-auto"><Trash2 className="h-4 w-4" />{t("sch.cancelEvent")}</Button>}
            <Button variant="outline" data-testid="event-check-btn" onClick={check}><CalendarCheck className="h-4 w-4" />{t("sch.check")}</Button>
            <Button data-testid="event-save-btn" onClick={save}>{t("common.save")}</Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Schedule() {
  const { t, lang } = useT();
  const { can } = useAuth();
  const [from, setFrom] = useState(() => startOfWeek(new Date()));
  const [span, setSpan] = useState(7);
  const [country, setCountry] = useState("");
  const [ev, setEv] = useState(null);
  const to = addDays(from, span);
  const { data: events, reload } = useFetch(`/schedule?start=${from.toISOString()}&end=${to.toISOString()}${country ? `&country=${country}` : ""}`);
  const { data: techs } = useFetch("/technicians");
  const { data: countries } = useFetch("/countries");
  const { data: allQuotes } = useFetch("/quotations");
  const quotes = (allQuotes || []).filter((q) => q.current_rev != null && ["sent", "accepted"].includes(q.status));
  const rows = (techs || []).filter((x) => !country || x.country === country);
  const days = useMemo(() => Array.from({ length: span }, (_, i) => addDays(from, i)), [from, span]);
  const visibleCountries = (countries || []).filter((c) => (techs || []).some((x) => x.country === c.code));
  const open = (tech, day, e) => {
    if (e) return setEv({ ...e, start: toLocal(e.start), end: toLocal(e.end) });
    if (!can("schedule:write")) return;
    const s = new Date(day); s.setHours(8, 0, 0, 0);
    const en = new Date(day); en.setHours(17, 0, 0, 0);
    setEv({ event_type: "assignment", technician_id: tech?.id || "", start: toLocal(s.toISOString()), end: toLocal(en.toISOString()), machine_ids: [] });
  };
  const fmtDay = (d) => d.toLocaleDateString(lang === "en" ? "en-US" : "es-MX", { weekday: "short", day: "2-digit", month: "short" });
  const narrow = useIsNarrow(1024);
  const evsFor = (techId, d) => {
    const ds = d.getTime(), de = addDays(d, 1).getTime();
    return (events || []).filter((e) => e.technician_id === techId && new Date(e.start).getTime() < de && new Date(e.end).getTime() > ds);
  };
  const chip = (tech, d, e) => (
    <button key={e.id} data-testid={`sch-event-${e.id}`} onClick={(x) => { x.stopPropagation(); open(tech, d, e); }}
      className={`mb-1 block w-full truncate rounded px-1.5 py-1 text-left text-[10px] font-semibold ${TONE[e.event_type]}`}>
      {hm(e.start)}–{hm(e.end)} · {e.folio || e.location || (e.absence_kind && t(`sch.kind.${e.absence_kind}`)) || t(`sch.type.${e.event_type}`)}
    </button>
  );
  const agenda = (
    <div className="space-y-3" data-testid="schedule-agenda">
      {days.map((d) => {
        const busy = rows.map((tech) => [tech, evsFor(tech.id, d)]).filter(([, l]) => l.length);
        return (
          <div key={d.toISOString()} className="rounded-md border border-neutral-200 bg-white">
            <div className={`flex items-center justify-between border-b border-neutral-200 px-3 py-2 text-xs font-bold capitalize ${[0, 6].includes(d.getDay()) ? "bg-neutral-100" : ""}`}>
              {fmtDay(d)}
              {can("schedule:write") && <button data-testid={`sch-add-${d.getDate()}`} onClick={() => open(null, d)} className="rounded-full p-1 text-[#db0720] hover:bg-red-50"><Plus className="h-4 w-4" /></button>}
            </div>
            <div className="space-y-2 p-3">
              {busy.length ? busy.map(([tech, l]) => (
                <div key={tech.id}><div className="mb-1 text-[11px] font-semibold">{tech.name} <span className="text-neutral-400">{tech.country}</span></div>{l.map((e) => chip(tech, d, e))}</div>
              )) : <div className="text-[11px] text-neutral-400">—</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
  return (
    <div>
      <PageHeader title={t("sch.title")} kicker={t("nav.group.ops")}>
        {can("schedule:write") && <Button data-testid="new-event-btn" onClick={() => open(null, new Date())}><Plus className="h-4 w-4" />{t("sch.newEvent")}</Button>}
      </PageHeader>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="flex items-center rounded-full border border-neutral-300 bg-white">
          <button data-testid="sch-prev-btn" onClick={() => setFrom(addDays(from, -span))} className="rounded-l-full p-2 hover:bg-neutral-100"><ChevronLeft className="h-4 w-4" /></button>
          <button data-testid="sch-today-btn" onClick={() => setFrom(startOfWeek(new Date()))} className="px-3 text-xs font-bold">{t("sch.today")}</button>
          <button data-testid="sch-next-btn" onClick={() => setFrom(addDays(from, span))} className="rounded-r-full p-2 hover:bg-neutral-100"><ChevronRight className="h-4 w-4" /></button>
        </div>
        <span className="text-sm font-semibold" data-testid="sch-range">{fmtDay(from)} – {fmtDay(addDays(to, -1))}</span>
        <div className="w-32"><NativeSelect testid="sch-span-select" value={String(span)} onChange={(v) => setSpan(Number(v))} options={[{ value: "7", label: t("sch.week") }, { value: "14", label: t("sch.twoWeeks") }]} /></div>
        <div className="w-48"><NativeSelect testid="sch-country-filter" value={country} placeholder={t("common.all")} onChange={(v) => setCountry(v || "")} options={visibleCountries.map((c) => ({ value: c.code, label: c.name }))} /></div>
        <div className="ml-auto flex flex-wrap gap-3 text-[11px]">
          {["assignment", "travel", "absence"].map((k) => <span key={k} className="flex items-center gap-1"><span className={`h-3 w-3 rounded-sm ${TONE[k]}`} />{t(`sch.type.${k}`)}</span>)}
        </div>
      </div>
      {narrow ? agenda : <div className="rounded-md border border-neutral-200 bg-white">
        <table className="w-full table-fixed border-collapse text-xs" data-testid="schedule-grid">
          <thead><tr>
            <th className="w-[150px] border-b border-r border-neutral-200 bg-neutral-100 px-3 py-2 text-left text-[11px] uppercase tracking-wider">{t("nav.technicians")}</th>
            {days.map((d) => <th key={d.toISOString()} className={`border-b border-r border-neutral-200 px-1.5 py-2 text-left text-[10px] font-semibold capitalize ${[0, 6].includes(d.getDay()) ? "bg-neutral-200/60" : "bg-neutral-100"}`}>{fmtDay(d)}</th>)}
          </tr></thead>
          <tbody>
            {rows.map((tech) => (
              <tr key={tech.id} data-testid={`sch-row-${tech.id}`}>
                <td className="border-b border-r border-neutral-200 bg-white px-3 py-2"><div className="truncate font-semibold">{tech.name}</div><div className="text-[10px] text-neutral-500">{tech.country} · {tech.level}</div></td>
                {days.map((d) => {
                  const ds = d.getTime();
                  const list = evsFor(tech.id, d);
                  return (
                    <td key={ds} onClick={() => open(tech, d)} data-testid={`sch-cell-${tech.id}-${d.getDate()}`}
                      className={`h-16 cursor-pointer overflow-hidden border-b border-r border-neutral-100 p-1 align-top transition-colors hover:bg-red-50/40 ${[0, 6].includes(d.getDay()) ? "bg-neutral-50" : ""}`}>
                      {list.map((e) => chip(tech, d, e))}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        {techs && !rows.length && <Empty>{t("common.empty")}</Empty>}
      </div>}
      {ev && <EventDialog ev={ev} techs={techs || []} quotes={quotes} onClose={() => setEv(null)} onSaved={() => { setEv(null); reload(); }} />}
    </div>
  );
}
